import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface TokenScore {
  id: string;
  token: {
    symbol: string;
    name: string;
    market_cap: number;
    current_price: number;
  };
  total_score: number;
  rating: string;
  momentum_score: number;
  position_score: number;
  technical_score: number;
  fundamentals_score: number;
  sentiment_score: number;
  risk_score: number;
  reasoning: string[];
  price_24h_change: number;
  volume_24h: number;
  score_date: string;
}

export default function TokenScoringDashboard() {
  const [scores, setScores] = useState<TokenScore[]>([]);
  const [loading, setLoading] = useState(false);
  const [runningScores, setRunningScores] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchScores = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('token_scores')
        .select(`
          *,
          token:tokens(symbol, name, market_cap, current_price)
        `)
        .eq('score_date', today)
        .order('total_score', { ascending: false })
        .limit(20);

      if (error) throw error;

      setScores(data || []);
      setLastUpdate(new Date().toLocaleString());
    } catch (error) {
      console.error('Error fetching scores:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch token scores',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const runDataFetch = async () => {
    setRunningScores(true);
    try {
      toast({
        title: 'Starting daily workflow',
        description: 'Running complete token analysis workflow...'
      });

      // Run the orchestrator function
      const { data, error } = await supabase.functions.invoke('daily-token-workflow');

      if (error) throw error;

      toast({
        title: 'Workflow complete!',
        description: `Collected ${data.summary?.tokens_collected || 0} tokens, scored ${data.summary?.tokens_scored || 0} tokens`
      });

      // Refresh the display
      await fetchScores();
      
    } catch (error) {
      console.error('Error running token workflow:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Workflow failed',
        variant: 'destructive'
      });
    } finally {
      setRunningScores(false);
    }
  };

  useEffect(() => {
    fetchScores();
  }, []);

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'HOT_PICK':
        return 'bg-red-500';
      case 'STRONG_CONTENDER':
        return 'bg-orange-500';
      case 'WATCHLIST':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getRatingEmoji = (rating: string) => {
    switch (rating) {
      case 'HOT_PICK':
        return '🔥';
      case 'STRONG_CONTENDER':
        return '⭐';
      case 'WATCHLIST':
        return '👀';
      default:
        return '❌';
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Token Scoring Dashboard</h1>
          {lastUpdate && (
            <p className="text-sm text-muted-foreground mt-1">Last updated: {lastUpdate}</p>
          )}
        </div>
        <div className="space-x-2">
          <Button onClick={fetchScores} disabled={loading} variant="outline">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
          <Button onClick={runDataFetch} disabled={runningScores}>
            {runningScores ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run Full Scoring
          </Button>
        </div>
      </div>

      {scores.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Scores Available</h3>
              <p className="text-muted-foreground mb-4">Run the scoring system to analyze tokens</p>
              <Button onClick={runDataFetch} disabled={runningScores}>
                Run Token Scoring
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scores.map((score) => (
          <Card key={score.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">
                    {score.token.symbol}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{score.token.name}</p>
                </div>
                <Badge className={getRatingColor(score.rating)}>
                  {getRatingEmoji(score.rating)} {score.rating.replace('_', ' ')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Total Score */}
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <div className="text-4xl font-bold text-primary">{score.total_score}</div>
                <div className="text-sm text-muted-foreground">Total Score</div>
              </div>

              {/* Price Info */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Price</p>
                  <p className="font-semibold">${score.token.current_price.toFixed(4)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">24h Change</p>
                  <p className={`font-semibold ${score.price_24h_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {score.price_24h_change >= 0 ? '+' : ''}
                    {score.price_24h_change?.toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Market Cap</p>
                  <p className="font-semibold">${(score.token.market_cap / 1_000_000).toFixed(1)}M</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Volume 24h</p>
                  <p className="font-semibold">${(score.volume_24h / 1_000_000).toFixed(1)}M</p>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="space-y-2">
                <p className="text-sm font-semibold">Score Breakdown:</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Momentum:</span>
                    <span className="font-semibold">{score.momentum_score}/30</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Position:</span>
                    <span className="font-semibold">{score.position_score}/20</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Technical:</span>
                    <span className="font-semibold">{score.technical_score}/20</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fundamentals:</span>
                    <span className="font-semibold">{score.fundamentals_score}/15</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sentiment:</span>
                    <span className="font-semibold">{score.sentiment_score}/10</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Risk:</span>
                    <span className="font-semibold">{score.risk_score}/5</span>
                  </div>
                </div>
              </div>

              {/* Reasoning */}
              {score.reasoning && score.reasoning.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-semibold">Why this score:</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {score.reasoning.slice(0, 3).map((reason, idx) => (
                      <li key={idx} className="flex items-start">
                        <TrendingUp className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0 text-green-500" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
